<template>
  <v-app id="app">
    <v-app-bar
      :color="`${toolbarColor} darken-4`"
      dark
      data-cy="appToolbar"
      dense
      fixed
      flat
      text
      v-if="!$route.meta.hideNavbar"
    >
      <v-toolbar-title>
        <router-link aria-label="Continuum home" data-cy="appToolbar__homeLink" to="/" class="white--text och-homeLink">
          <img alt="" class="och-logo" role="presentation" src="/ochin-logo-scaled.png" />
          <span>CONTINUUM</span>
        </router-link>
      </v-toolbar-title>
      <v-spacer></v-spacer>

      <v-toolbar-items v-if="loggedIn">
        <v-btn data-cy="appToolbar__searchLink" text @click="$router.push({ path: '/search' })"
          ><v-icon class="mr-1 mt-0" size="20">fas fa-search</v-icon>Search</v-btn
        >
      </v-toolbar-items>

      <v-menu :color="`${toolbarColor} darken-4`" data-cy="appToolbar__userMenu" offset-y v-if="userName">
        <template v-slot:activator="{ on }">
          <v-btn :color="`${toolbarColor} darken-4`" data-cy="appToolbar__userName" large v-on="on" class="elevation-0">
            <v-icon class="mr-2">fas fa-user-circle</v-icon>
            <span class="elevation-0">{{ userName }}</span>
            <v-icon dark>arrow_drop_down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-if="hasAdminAny"
            data-cy="appToolbar__adminLink"
            text
            @click="$router.push({ path: '/admin/menu' })"
            >Admin</v-list-item
          >
          <v-list-item
            v-if="hasReleaseNoteCreateEdit && isOchinUser"
            data-cy="appToolbar__qaJiraImportLink"
            text
            @click="$router.push({ path: '/jira-import' })"
            >QA Import</v-list-item
          >
          <v-list-item text href="https://ella.ochin.org/ip/mod/data/view.php?d=1&rid=3046" target="_blank"
            >User Guide
          </v-list-item>
          <v-list-item
            v-if="isOchinUser"
            href="https://app.smartsheet.com/b/publish?EQBCT=ea6c0a44d796416cbdafa7fdd333c4da"
            target="_blank"
            >Roadmap</v-list-item
          >
          <v-list-item
            v-else-if="!isOchinUser"
            href="https://app.smartsheet.com/b/publish?EQBCT=ba8321bd07b44ebbb5a82bc97738f40f"
            target="_blank"
            >Roadmap</v-list-item
          >
          <v-list-item href="https://app.smartsheet.com/b/form/dc4d2e55c7b744dd9bbdf66978c933c5" target="_blank"
            >Enhancement Request Form</v-list-item
          >
          <v-list-item data-cy="appToolbar__logoutLink" @click="logout">
            <v-list-item-title>Logout <v-icon>exit_to_app</v-icon></v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <div
      v-if="isTestEnv"
      style="background-color: yellow; color: black; margin-top: 50px; padding: 10px; text-align: center; font-weight:bold; font-size: 24px;"
    >
      TEST ENVIRONMENT -- DO NOT USE FOR PRODUCTION -- TEST ENVIRONMENT -- DO NOT USE FOR PRODUCTION -- TEST ENVIRONMENT
    </div>

    <v-main class="mt-5">
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import { removeCookie } from "tiny-cookie";
import { mapActions, mapGetters, mapState } from "vuex";
import UserPicker from "./components/userPicker.vue";
import user from "./api/user";

const toolbarColorMap = {
  demo: "purple",
  development: "green",
  qa: "cyan",
  staging: "orange",
};

export default {
  name: "app",
  components: {},
  computed: {
    isTestEnv() {
      return process.env.VUE_APP_ENV === "test";
    },
    theme() {
      return this.$store.state.session.theme;
    },
    toolbarColor() {
      let env = this.env;

      if (!env) {
        return this.theme.primaryColor;
      }

      if (env === "production") {
        return this.theme.primaryColor;
      }

      return toolbarColorMap[env];
    },
    userName() {
      const userPrincipalName = this.$store.state.session.user.userPrincipalName;

      if (!userPrincipalName) {
        return "";
      }

      if (userPrincipalName && userPrincipalName.includes("@")) {
        return userPrincipalName.split("@")[0];
      }
      return userPrincipalName;
    },
    ...mapGetters({
      hasAdminAny: "session/hasAdminAny",
      hasReleaseNoteCreateEdit: "session/hasReleaseNoteCreateEdit",
    }),
    ...mapState({
      env: state => state.application.env,
      loggedIn: state => state.session.loggedIn,
      isOchinUser: state => state.session.user.isOchinUser,
    }),
  },
  async created() {
    if (this.env) {
      return;
    }
    await this.loadEnv();
  },
  methods: {
    logout() {
      removeCookie("id_token");
      removeCookie("jwtContinuum");
      removeCookie("refresh_token");
      const logout = encodeURIComponent(`${location.origin}/api/logout`);

      const logoutUrl = `https://continuum.auth.us-west-2.amazoncognito.com/logout?client_id=36a82ijra5b9neca1rmtivnalm&logout_uri=${logout}`;

      document.location = logoutUrl;
    },
    ...mapActions({
      loadEnv: "application/loadEnv",
    }),
  },
};
</script>

<style>
/* Global custom CSS styles can be stored in an unscoped style tag on the body element in App.vue.
  postcss-https://preset-env.cssdb.org/features
  */

body {
  --fontFamily-primary: "Avenir", Helvetica, Arial, sans-serif;
  font-family: var(--fontFamily-primary);
}

/* Override Vuetify default v-card text color */
.theme--light.v-card > .v-card__text {
  color: black !important;
}

/* Override Vuetify tooltip opacity to improve legibility; default is .90 */
.v-tooltip__content.menuable__content__active {
  opacity: 1 !important;
}

/* Remove bottom margin from last block element inside tooltips so vertical
   spacing is even (e.g. summaries that end with a <p> tag). */
.v-tooltip__content span > *:last-child {
  margin-bottom: 0;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.och-border--all {
  border: 1px solid #cccccc;
}

.och-homeLink {
  text-decoration: none;
  font-weight: 300;
}

.och-homeLink:hover {
  opacity: 0.8;
}

.och-logo {
  vertical-align: middle;
  margin-right: 8px;
}

.och-topMargin--packInfoOffset {
  margin-top: 110px !important;
}

.pointer {
  cursor: pointer;
}

.spinner {
  animation: spinner 2s infinite linear;
}
@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}

@media only screen and (max-width: 740px) {
  .och-topMargin--packInfoOffset {
    margin-top: 145px !important;
  }
}

@media only screen and (max-width: 500px) {
  .och-topMargin--packInfoOffset {
    margin-top: 170px !important;
  }
}

.och-pre--scrollable {
  overflow: auto;
}

.och-testPlanCard__border--bottom {
  /* override Vuetify */
  border-bottom: 1px solid #ccc !important;
}

.och-testPlanCard__border--top {
  /* override Vuetify */
  border-top: 1px solid #ccc !important;
}

.och-testPlanCard__border--right {
  /* override Vuetify */
  border-right: 1px solid #ccc !important;
}

.v-btn.och-button--usermenu {
  border: 1px solid !important;
}

.v-btn.theme--dark.och-button--usermenu:hover,
.v-btn.theme--dark.och-button--usermenu:focus {
  border: 1px dotted white !important;
}

.v-input__icon--clear button {
  font-size: 16px !important;
}

.v-data-table-header th {
  white-space: nowrap !important;
}
</style>
